"use client"

import { useEffect, useRef } from "react"
import { trackZoneImpression } from "@/lib/ad-zone-tracking"

/**
 * Reports exactly one network-zone impression when the ad it sits inside
 * actually becomes VIEWABLE. Renders no visible output.
 *
 * Why a component instead of a hook in the ad itself: most ad components
 * decide whether to render through a chain of early returns (consent,
 * geometry, size floors). A useEffect in that kind of component must go
 * either before the gates — firing for ads that never rendered — or after
 * them, which is a Rules-of-Hooks violation; inline-grid-ad.tsx carries a
 * comment about that exact crash ("React detected a change in the order of
 * Hooks"). Mounting this inside the returned JSX avoids both.
 *
 * Why viewability and not mount: the browse grid injects ads throughout a
 * long scrolling list, so counting on mount books an impression for every ad
 * below the fold that the viewer never reaches. Measured during this fix: 13
 * rendered grid ads produced 20 mount-based impressions. Counting on
 * intersection instead matches what inline-grid-ad.tsx already does
 * (threshold 0.5) and is what "impression" is normally understood to mean.
 *
 * Observes the PARENT node, since this component renders a zero-size marker
 * and a 0x0 box can never satisfy a 0.5 threshold on its own.
 */
export function ZoneImpressionBeacon({ zoneId }: { zoneId?: string | null }) {
  const markerRef = useRef<HTMLSpanElement | null>(null)
  const fired = useRef(false)

  useEffect(() => {
    if (fired.current || !zoneId) return
    const target = markerRef.current?.parentElement
    if (!target || typeof IntersectionObserver === "undefined") {
      // No observer available (SSR/jsdom) — fall back to counting the render
      // rather than losing the impression entirely.
      fired.current = true
      trackZoneImpression({ zoneId })
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !fired.current) {
          fired.current = true
          trackZoneImpression({ zoneId })
          observer.disconnect()
        }
      },
      { threshold: 0.5 },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [zoneId])

  return <span ref={markerRef} aria-hidden className="hidden" />
}
